import { Link } from "react-router-dom";
import { useState } from "react";
import { ArrowRight, Sparkles, Star, MessageSquare, Youtube, Instagram } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageLayout } from "@/components/layout/PageLayout";
import chefXMascot from "@/assets/chef-x-mascot.png";

export default function Home() {
  const [videoStarted, setVideoStarted] = useState(false);

  return (
    <PageLayout>
      {/* Hero Section with Video Background */}
      <section className="relative overflow-hidden min-h-[80vh] flex items-center">
        {/* Video Background */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-background/80 z-10" />
          {videoStarted ? (
            <iframe
              src="https://www.youtube.com/embed/03gCdy55X24?autoplay=1&mute=1&loop=1&playlist=03gCdy55X24&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1"
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[200%] h-[200%] pointer-events-none border-0"
              allow="autoplay; encrypted-media"
              title="Background video"
            />
          ) : (
            <button
              onClick={() => setVideoStarted(true)}
              className="absolute inset-0 w-full h-full bg-muted/50 cursor-default"
              aria-label="Load background video"
              tabIndex={-1}
            />
          )}
        </div>

        <div className="container py-16 md:py-24 relative z-10">
          <div className="mx-auto max-w-4xl text-center">
            {/* Main headline */}
            <h1 className="font-display text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl text-foreground">
              Meet{" "}
              <span className="text-primary">
                Kitchen Assistant X
              </span>
            </h1>

            {/* Subheadline */}
            <p className="mt-4 font-display text-xl text-muted-foreground sm:text-2xl">
              Your Personal AI Chef
            </p>

            {/* Tagline */}
            <p className="mt-6 text-lg text-muted-foreground max-w-xl mx-auto">
              Ask. Cook. Learn. Eat better in minutes. Get personalized recipes using what you already have.
            </p>

            {/* CTA */}
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button asChild size="lg" className="text-lg px-8 h-14 rounded-full">
                <Link to="/chat">
                  Try it Now
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="h-14 rounded-full bg-background/80">
                <Link to="/how-it-works">See How It Works</Link>
              </Button>
            </div>

            {/* Social Links */}
            <div className="mt-8 flex items-center justify-center gap-4">
              <a 
                href="https://youtube.com/@kitchenassistantx" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 rounded-full bg-background/80 px-4 py-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
              >
                <Youtube className="h-5 w-5" />
                YouTube
              </a>
              <a 
                href="https://instagram.com/kitchenassistantx" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 rounded-full bg-background/80 px-4 py-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
              >
                <Instagram className="h-5 w-5" />
                Instagram
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="border-t bg-muted/30 py-20">
        <div className="container">
          <h2 className="font-display text-3xl font-bold text-center mb-12">
            Cook Smarter, Not Harder
          </h2>

          <div className="grid gap-8 md:grid-cols-3">
            {/* Feature 1 */}
            <div className="relative rounded-2xl border bg-card p-8 text-center transition-shadow hover:shadow-lg">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
                <MessageSquare className="h-7 w-7 text-primary" />
              </div>
              <h3 className="font-display text-xl font-semibold mb-2">
                Tell X What You Have
              </h3>
              <p className="text-muted-foreground">
                Just list your ingredients or tell X what you're craving. No need for complex searches.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="relative rounded-2xl border bg-card p-8 text-center transition-shadow hover:shadow-lg">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-secondary/10">
                <Sparkles className="h-7 w-7 text-secondary" />
              </div>
              <h3 className="font-display text-xl font-semibold mb-2">
                Get Personalized Recipes
              </h3>
              <p className="text-muted-foreground">
                Receive clear, step-by-step recipes tailored to your ingredients and preferences.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="relative rounded-2xl border bg-card p-8 text-center transition-shadow hover:shadow-lg">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-accent/30">
                <Star className="h-7 w-7 text-accent-foreground" />
              </div>
              <h3 className="font-display text-xl font-semibold mb-2">
                Save Your Favorites
              </h3>
              <p className="text-muted-foreground">
                Star the recipes you love and build your personal cookbook over time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container">
          <div className="mx-auto max-w-2xl rounded-3xl bg-muted/50 border p-8 md:p-12 text-center">
            <img 
              src={chefXMascot} 
              alt="Chef X" 
              className="h-24 w-24 mx-auto mb-6 object-contain rounded-full"
            />
            <h2 className="font-display text-2xl md:text-3xl font-bold mb-4">
              Ready to Cook Something Amazing?
            </h2>
            <p className="text-muted-foreground mb-8">
              X is here to help you make the most of what's in your kitchen.
            </p>
            <Button asChild size="lg" className="rounded-full">
              <Link to="/chat">
                Start Cooking with X
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
